import React, { FC, PropsWithChildren } from "react";
import {
  QueryClient,
  QueryClientProvider,
  QueryCache,
} from "@tanstack/react-query";

//
import {
  V3PlayerFilters,
  PlayerActivityFilters,
} from "@neuromotion-inc/mightier-ts-sdk";

import * as Sentry from "@sentry/react";

type QueryKey =
  | ["players", "list", V3PlayerFilters]
  | ["players", "retrieve", number]
  | [
      "players",
      "retrieve",
      number,
      "activity",
      PlayerActivityFilters | undefined,
    ]
  //
  | ["password-reset-validate", { token: string | undefined; uidb64: string }]
  //
  | ["survey-config", string, string]
  | ["USER_INFO"]
  | ["USER_INFO_VIA_OAUTH_TOKEN"]
  //
  | ["CREATE_ACCOUNT"]
  //
  | ["game-definitions"];

type MutationKey =
  // account activation feature
  | ["PATCH_USER", number | undefined]
  | ["PATCH_USER_NOTIFICATION_PREFERENCES", number | undefined]
  | ["USER_SET_PASSWORD"]
  | ["COMPLETE_ACCOUNT_SETUP", number | undefined]
  // password reset feature
  | ["password-reset"]
  | ["password-reset-complete"]
  // surveyt engine feature
  | ["SURVEY_ENGINE_SUBMIT_STEP"]
  | ["SURVEY_ENGINE_ANALYTIC"]
  //
  | ["SESSION_LOGIN"]
  | ["SESSION_LOGOUT"];

declare module "@tanstack/react-query" {
  // eslint-disable-next-line no-unused-vars
  interface Register {
    queryKey: QueryKey;
    mutationKey: MutationKey;
  }
}

const TanStackQueryDevtools =
  process.env.NODE_ENV === "production"
    ? (): null => null // Render nothing in production
    : React.lazy(() =>
        // Lazy load in development
        import("@tanstack/react-query-devtools").then((res) => ({
          default: res.ReactQueryDevtools,
          // For Embedded Mode
          // default: res.TanStackRouterDevtoolsPanel
        })),
      );

export const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      // default - queries will be refetched if same query key is mounted again (by same or another component)
      // after 60s
      staleTime: 60 * 1000,
    },
  },
  queryCache: new QueryCache({
    onError: (error, query): void => {
      Sentry.captureException(error, { extra: { queryKey: query.queryKey } });
    },
  }),
});

export const TanstackQuerySetup: FC<PropsWithChildren> = ({ children }) => {
  return (
    <QueryClientProvider client={queryClient}>
      {children}
      <TanStackQueryDevtools />
    </QueryClientProvider>
  );
};
